import { DataPointDropDownService, WebPictureNaviExtensionService } from '@datapoints';
import {
    PlantManageUsersAccessService,
    PlantNotificationsAccessService,
    PlantSettingsAccessService,
    PlantOnlineIconComponent,
    PlantAlarmIconComponent,
    PlantTaskIconComponent,
    PlantUpgradeFilePriorityIconComponent,
    PlantUpgradeSubnavigationIconsComponent
} from '@plants';
import { SubnaviDropDownService } from '@navigation';
import { __asyncI18n } from '@localisation';
import { PlantDashboardNaviExtension, PlantDashboardDropdownService, PlantDashboardComponent } from '@dashboards';
import { RoutingEntry, RoutingLoadableFiles } from '@routing';
import { PlantApplicationSetNaviExtension } from '@plants';
import { RoutePrivilegeNames } from './routeprivilegenames';
import { DummySiteComponent } from '@site-info';
import { UpgradeComponent } from '@upgrade';
import { AlarmComponent } from '@alarm';
import { WebAccessScreen, WebAccessDynamicExternalLink } from '@web-access';
import { HistoryComponent } from '@history';
import { TaskListComponent } from '@task';
import { SchedulerComponent } from '@scheduler';
import { PlantFileListComponent,  PlantFileDetails } from '@files';
import { CanActivateWithCurrentTenant } from '@plants';

export const PlantOperatingRoutes: Array<RoutingEntry> = [
    {
        path: ':plantid',
        data: {
            title: ':plantid | plantIdToName',
            dontShowInSubNavigation: true,
            breadCrumbDropDown: SubnaviDropDownService,
            favouritesRouteTitle: '[SiteName]',
            //lastSubNaviLevel : true,
            redirectTo: [
                'Operating/Plants/:plantid/PlantDashboard/*firstChild',
                'Operating/Plants/:plantid/DataPoints',
                'Operating/Plants/:plantid/Alarms',
                'Operating/Plants/:plantid/WebAccess',
                'Operating/Plants/:plantid/WebPicture',
                'Operating/Plants/:plantid/Upgrade',
                'Operating/Plants/:plantid/History',
                'Operating/Plants/:plantid/Scheduler',
                'Operating/Plants/:plantid/Tasks',
                'Operating/Plants/:plantid/Documentation',
                'Operating/Plants/:plantid/PlantSettings',
                'Operating/Plants/:plantid/PlantUsers',
                'Operating/Plants/:plantid/PlantNotifications'
            ]
        },
        children: [
            {
                path: '',
                canActivate: [CanActivateWithCurrentTenant],
                component: DummySiteComponent,
                data: {
                    title: ':plantid | plantIdToName',
                    dontShowInSubNavigation: true,
                    breadCrumbDropDown: SubnaviDropDownService,
                    favouritesRouteTitle: '[SiteName]',
                    //lastSubNaviLevel : true,
                    redirectTo: [
                        'Operating/Plants/:plantid/PlantDashboard/*firstChild',
                        'Operating/Plants/:plantid/DataPoints',
                        'Operating/Plants/:plantid/Alarms',
                        'Operating/Plants/:plantid/WebAccess',
                        'Operating/Plants/:plantid/WebPicture',
                        'Operating/Plants/:plantid/Upgrade',
                        'Operating/Plants/:plantid/History',
                        'Operating/Plants/:plantid/Scheduler',
                        'Operating/Plants/:plantid/Tasks',
                        'Operating/Plants/:plantid/Documentation',
                        'Operating/Plants/:plantid/PlantSettings',
                        'Operating/Plants/:plantid/PlantUsers',
                        'Operating/Plants/:plantid/PlantNotifications'
                    ]
                }
            },
            {
                path: 'PlantDashboard',
                data: {
                    title: __asyncI18n('Plant dashboard', 'subnavi.operating.plantdashboard'),
                    hideWithoutChildren: true,
                    dynamicChildren: PlantDashboardNaviExtension,
                    showChildrenAsSecondLevel: true,
                    breadCrumbDropDown: PlantDashboardDropdownService,
                    redirectTo: 'Operating/Plants/:plantid/PlantDashboard/*firstChild',
                    testId: 'sidenavigation.link.plantdashboard',
                    privileges: [RoutePrivilegeNames.PlantDashboard]
                },
                children: [
                    {
                        path: ':dashboardid',
                        data: {
                            title: ':dashboardid | plantDashboardIdToName',
                            contentRoute: '/Site/Site/Dashboard/:plantid/?pageId=:dashboardid&tas=:tas',
                            contentRouteParams : {
                                tas : '->isTenantDashboard:plantid:dashboardid'
                            },
                 
                            dontShowInSubNavigation: true,
                            hideWithoutChildren: true,
                            favouritesCaption: '[PlantDashboardName]'
                        },
                        children: [
                            {
                                path: '',
                                component: PlantDashboardComponent,
                                data: {
                                    title: ':dashboardid | plantDashboardIdToName',
                                    contentRoute: '/Site/Site/Dashboard/:plantid?pageId=:dashboardid&tas=:tas',
                                    contentRouteParams : {
                                        tas : '->isTenantDashboard:plantid:dashboardid'
                                    },
                                    dontShowInSubNavigation: true,
                                    hideWithoutChildren: true,
                                    favouritesCaption: '[PlantDashboardName]'
                                }
                            },
                            {
                                path: 'Trend',
                                data: {
                                    title: __asyncI18n('Trend', 'subnavi.operating.plantdashboard.trend'),
                                    dontShowInSubNavigation: true,
                                    showSubNaviLevel: 3,
                                    dontShowInBreadCrumb: true
                                },
                                children: [
                                    {
                                        path: ':tileid',
                                        loadChildren: () => import('../modules/viewnodes-module/index').then(m => m.ViewNodesModule),
                                        data: {
                                            title: ':tileid | plantTileIdToName',
                                            dontShowInSubNavigation: true,
                                            lazyRedirectTo: 'Operating/Plants/:plantid/PlantDashboard/:dashboardid/Trend/:tileid/PlantView',
                                            showSubNaviLevel: 4
                                        }
                                    }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                path: 'DataPoints',
                loadChildren: () => import('../modules/viewnodes-module/index').then(m => m.ViewNodesModule),
                data: {
                    title: __asyncI18n('Data points', 'subnavi.operating.datapoints'),
                    statusService: {
                        component: PlantOnlineIconComponent,
                        parameter: {
                            plantId: ':plantid'
                        }
                    },
                    breadCrumbDropDown: DataPointDropDownService,
                    testId: 'sidenavigation.link.datapoints',
                    favouritesRouteColor: 'stone',
                    privileges: [ RoutePrivilegeNames.CloudItem ]
                }
            },
            {
                path: 'Alarms',
                component: AlarmComponent,
                data: {
                    title: __asyncI18n('Alarms', 'subnavi.operating.alarms'),
                    statusService: {
                        component: PlantAlarmIconComponent,
                        parameter: {
                            plantId: ':plantid'
                        }
                    },
                    testId: 'sidenavigation.link.alarms',
                    privileges: [RoutePrivilegeNames.PlantAlarm]
                }
            },
            {
                path: 'WebAccess',
                data: {
                    title: __asyncI18n('Web access', 'subnavi.operating.webaccess'),
                    testId: 'sidenavigation.link.webaccess',
                    privileges: [RoutePrivilegeNames.WebAccess],
                    redirectTo: 'Operating/Plants/:plantid/WebAccess/false',
                    externalLink: WebAccessDynamicExternalLink
                },
                children: [
                    {
                        path: ':openEnergyIndicatorPage',
                        component: WebAccessScreen,
                        data: {
                            title: __asyncI18n('Web access', 'subnavi.operating.webaccess'),
                            privileges: [RoutePrivilegeNames.WebAccess],
                            dontShowInSubNavigation: true,
                            dontShowInBreadCrumb: true,
                            showSubNaviLevel: 3
                        }
                    }
                ]
            },
            {
                path: 'WebPicture',
                data: {
                    title: __asyncI18n('Web graphic', 'subnavi.operating.webpicture'),
                    hideWithoutChildren: true,
                    dynamicChildren: WebPictureNaviExtensionService,
                    showChildrenAsSecondLevel: true,
                    redirectTo: 'Operating/Plants/:plantid/WebPicture/*firstChild',
                    testId: 'sidenavigation.link.webpicture',
                    privileges: [RoutePrivilegeNames.PlantGraphic]
                },
                children: [
                    {
                        path: ':webpictureid',
                        data: {
                            title: ':webpictureid',
                            redirectTo: 'Operating/Plants/:plantid/WebPicture/:webpictureid/View',
                            lazyRedirectTo: 'Operating/Plants/:plantid/WebPicture/:webpictureid/View',
                            dontShowInSubNavigation: true,
                            dontShowInBreadCrumb: true,
                            hideWithoutChildren: true,
                        },
                        children: [
                            {
                                path: 'View',
                                component: DummySiteComponent,
                                data: {
                                    title: 'View',
                                    redirectTo: 'Operating/Plants/:plantid/WebPicture/*firstChild',
                                    dontShowInSubNavigation: true,
                                    dontShowInBreadCrumb: true,
                                    showSubNaviLevel : 4,
                                    favouritesType: 'webpicture'
                                }
                            }
                        ]
                    }
                ]
            },
            {
                path: 'Upgrade',
                component: UpgradeComponent,
                data: {
                    title: __asyncI18n('Upgrade', 'subnavi.operating.upgrade'),
                    testId: 'sidenavigation.link.upgrade',
                    privileges: [RoutePrivilegeNames.PlantUpgrade],
                    statusService: {
                        component: PlantUpgradeSubnavigationIconsComponent,
                        parameter: {
                            plantId: ':plantid'
                        }
                    },
                }
            },
            {
                path: 'History',
                component: HistoryComponent,
                data: {
                    title: __asyncI18n('History', 'subnavi.operating.history'),
                    testId: 'sidenavigation.link.history',
                    privileges: [RoutePrivilegeNames.PlantHistory]
                }
            },
            {
                path: 'Scheduler',
                component: SchedulerComponent,
                data: {
                    title: __asyncI18n('Scheduler', 'subnavi.operating.scheduler'),
                    files: [
                        { path: '/bundles/ba/scheduler', type: RoutingLoadableFiles.js }
                    ],
                    testId: 'sidenavigation.link.scheduler',
                    privileges: [RoutePrivilegeNames.PlantScheduler]
                }
            },
            {
                path: 'Tasks',
                component: TaskListComponent,
                data: {
                    title: __asyncI18n('Tasks', 'subnavi.operating.task'),
                    contentRoute: '/Site/Site/Tasks/:plantid',
                    testId: 'sidenavigation.link.tasks',
                    privileges: [RoutePrivilegeNames.PlantTasks],
                    statusService: {
                        component: PlantTaskIconComponent,
                        parameter: {
                            plantId: ':plantid'
                        }
                    }
                }
            },
            {
                path: 'Files',
                data: {
                    title: __asyncI18n('Files', 'subnavi.operating.systems.files'),
                    testId: 'sidenavigation.link.systemfiles',
                    privileges: [
                        RoutePrivilegeNames.PlantFileAccess,
                        RoutePrivilegeNames.PlantFileManage
                    ]
                },
                children: [
                    {
                        path: '',
                        component: PlantFileListComponent,
                        data: {
                            title: __asyncI18n('Files', 'subnavi.operating.systems.files'),
                            showSubNaviLevel: 3
                        }
                    },
                    {
                        path: 'Add',
                        data: {
                            title: __asyncI18n('Add file', 'subnavi.operating.systems.file.add'),
                            showSubNaviLevel: 3,
                        },
                        children: [
                            {
                                path: '',
                                component: PlantFileDetails,
                                data: {
                                    title: __asyncI18n('Add file', 'subnavi.operating.systems.file.add'),
                                    showSubNaviLevel: 3
                                }
                            },
                            {
                                path: ':type',
                                component: PlantFileDetails,
                                data: {
                                    title: __asyncI18n('Add Parameter file', 'subnavi.operating.systems.file.add.parameterfile'),
                                    showSubNaviLevel: 3,
                                    dontShowInBreadCrumb: true

                                }
                            }
                        ]
                    },
                    {
                        path: ':fileid',
                        data: {
                            title: ':fileid | fileIdToName',
                            dontShowInSubNavigation: true,
                            showSubNaviLevel: 3,
                            favouritesCaption: '[ParentPageName] / [PageName]',
                        },
                        children: [
                            {
                                path: '',
                                component: PlantFileDetails,
                                data: {
                                    title: ':fileid | fileIdToName',
                                    dontShowInSubNavigation: true,
                                    showSubNaviLevel: 3,
                                    favouritesCaption: '[ParentPageName] / [PageName]'
                                }
                            }
                        ]
                    }
                ]
            },
            {
                path: 'PlantSettings',
                component: DummySiteComponent,
                canActivate: [ PlantSettingsAccessService ],
                data: {
                    redirectTo: '/Administration/Plants/:plantid/Settings',
                    title: __asyncI18n('Plant settings', 'subnavi.operating.plantsettings'),
                    testId: 'sidenavigation.link.plantsettings',
                }
            },
            {
                path: 'PlantUsers',
                component: DummySiteComponent,
                canActivate: [ PlantManageUsersAccessService ],
                data: {
                    redirectTo: 'Administration/Plants/:plantid/Users',
                    title: __asyncI18n('Plant users', 'subnavi.administration.plant.plant.users'),
                    testId: 'sidenavigation.link.plantusers'
                }
            },
            {
                path: 'PlantNotifications',
                component: DummySiteComponent,
                canActivate: [ PlantNotificationsAccessService ],
                data: {
                    redirectTo: 'Administration/Plants/:plantid/Alarms',
                    dynamicSiblings: PlantApplicationSetNaviExtension,
                    title: __asyncI18n('Plant notifications', 'subnavi.administration.plant.alarms'),
                    testId: 'sidenavigation.link.plantnotifications'
                }
            }

        ]
    }
];
